import { MAX_FOLDER_DEPTH } from '../../../../constants/folder';

interface DepthNode {
  id: string;
  parentId: string | null;
}

const MAX_WALK = MAX_FOLDER_DEPTH + 5;

const depthCache = new WeakMap<object, Map<string, number>>();

export const getNodeDepth = <T extends DepthNode>(
  nodes: Record<string, T>,
  nodeId: string,
): number => {
  let cache = depthCache.get(nodes);
  if (!cache) {
    cache = new Map();
    depthCache.set(nodes, cache);
  }
  const cached = cache.get(nodeId);
  if (cached !== undefined) return cached;

  let depth = 0;
  let cursorId: string | null = nodeId;
  const seen = new Set<string>();
  while (cursorId && depth < MAX_WALK) {
    const node = nodes[cursorId];
    if (!node || seen.has(cursorId)) break;
    seen.add(cursorId);
    depth += 1;
    cursorId = node.parentId;
  }
  cache.set(nodeId, depth);
  return depth;
};

export const getSubtreeMaxDepthIndexed = (
  childrenByParent: Record<string, string[]>,
  rootId: string,
): number => {
  let maxDepth = 1;
  const queue: Array<{ id: string; depth: number }> = [
    { id: rootId, depth: 1 },
  ];
  while (queue.length > 0) {
    const current = queue.shift();
    if (!current) break;
    if (current.depth > maxDepth) maxDepth = current.depth;
    if (current.depth >= MAX_WALK) continue;
    const children = childrenByParent[current.id] ?? [];
    for (const childId of children) {
      queue.push({ id: childId, depth: current.depth + 1 });
    }
  }
  return maxDepth;
};

export const wouldExceedDepth = <T extends DepthNode>(
  nodes: Record<string, T>,
  parentId: string | null,
  subtreeDepth: number,
): boolean => {
  const parentDepth = parentId === null ? 0 : getNodeDepth(nodes, parentId);
  return parentDepth + subtreeDepth > MAX_FOLDER_DEPTH;
};
